import type { TransitionStatus } from 'react-transition-group';
import styled, { css } from 'styled-components';
import breakpoints from 'styles/config/breakpoints';
import themeColors from 'styles/config/colors';
import sizes from 'styles/config/sizes';
import themeSpacing from 'styles/config/spacing';
import text from 'styles/config/text';

import { Body } from 'components/atoms/LegacyText';

export type ActionBarCornerVariant = 'rounded' | 'pill';

export const ActionDrawerWrapper = styled.div<{
  $state: TransitionStatus;
  $mainNavOpen: boolean;
}>`
  pointer-events: none;

  position: fixed;
  z-index: 29;
  right: 0;
  bottom: 0;
  left: ${({ $mainNavOpen }) => ($mainNavOpen ? `${sizes.mainNavWidth}px` : 0)};

  display: flex;
  align-items: center;
  justify-content: center;

  padding: ${themeSpacing.sm4} ${themeSpacing.md2};

  @media (max-width: ${breakpoints.md}px) {
    padding: ${themeSpacing.sm4};
  }

  @media (max-width: ${breakpoints.sm}px) {
    padding: ${themeSpacing.sm4} ${themeSpacing.sm3};
  }
`;

export const Container = styled.div<{
  $state: TransitionStatus;
  $barHeight: number;
}>`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: center;
  justify-content: start;

  transition: max-height 0.25s ease-in-out;

  ${({ $state, $barHeight }) => {
    switch ($state) {
      case 'entering':
      case 'exited':
      case 'exiting':
        return css`
          max-height: ${$barHeight}px;
        `;
      case 'entered':
        return css`
          max-height: 128px;
        `;
    }
  }};
`;

export const ForceWidth = styled.div<{
  $width?: number;
}>`
  ${({ $width }) =>
    $width &&
    css`
      width: ${$width}px;
      max-width: 100%;
      margin: 0 auto;
    `}
`;

export const SecondaryAction = styled.div`
  pointer-events: all;
  margin: ${themeSpacing.xs3} ${themeSpacing.sm3} ${themeSpacing.xs3} 0;
`;

export const ActionBar = styled.div<{
  $cornerVariant: ActionBarCornerVariant;
  $state: TransitionStatus;
  $barHeight?: number;
  $hasText?: boolean;
}>`
  pointer-events: all;

  display: flex;
  gap: ${themeSpacing.sm4};
  align-items: center;
  justify-content: center;
  justify-self: center;

  max-width: 100%;
  padding: ${themeSpacing.xs3} ${themeSpacing.sm1};

  color: ${themeColors.textPrimaryInverse};

  background-color: ${themeColors.backgroundInverse};
  border-radius: ${({ $cornerVariant }) =>
    $cornerVariant === 'pill' ? themeSpacing.radiusFull : themeSpacing.xs1};

  transition: transform 0.25s ease-in-out, opacity 0.25s ease-in-out;

  button {
    white-space: nowrap;
  }

  ${({ $state, $barHeight }) => {
    switch ($state) {
      case 'exited':
      case 'entering':
      case 'exiting':
        return css`
          transform: translateY(${$barHeight}px);
          opacity: 0;
        `;
      case 'entered':
        return css`
          transform: translateY(0);
          opacity: 1;
        `;
    }
  }};

  ${({ $hasText }) =>
    !$hasText &&
    css`
      padding: ${themeSpacing.xs3};
    `}
`;

export const Text = styled(Body)<{ $cornerVariant: ActionBarCornerVariant }>`
  padding-left: ${({ $cornerVariant }) =>
    $cornerVariant === 'pill' ? themeSpacing.xs2 : themeSpacing.none};
  font-weight: ${text.weight.semibold};
`;

export const Buttons = styled.div`
  display: flex;
  gap: ${themeSpacing.xs3};
`;

export const FakeActionDrawer = styled.div<{
  $drawerHeight: number;
  $state: TransitionStatus;
}>`
  transition: min-height 0.25s ease-in-out;
  ${({ $state, $drawerHeight }) => {
    switch ($state) {
      case 'entering':
      case 'exited':
        return css`
          min-height: 0;
        `;
      case 'entered':
      case 'exiting':
        return css`
          min-height: calc(${$drawerHeight}px - ${themeSpacing.md2});

          @media (max-width: ${breakpoints.md}px) {
            min-height: calc(${$drawerHeight}px - ${themeSpacing.sm4});
          }

          @media (max-width: ${breakpoints.sm}px) {
            min-height: calc(${$drawerHeight}px - ${themeSpacing.sm3});
          }
        `;
    }
  }};
`;
